import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { AdUnitType, TemplateCategory } from "@shared/schema";
import { CATEGORY_COLOR_OPTIONS, CATEGORY_ICON_OPTIONS, getCategoryVisual } from "@/lib/bucket-visuals";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdImage } from "@/components/ad-image";
import { displayUrl, normalizeExampleImage } from "@shared/adImage";
import { Plus, ChevronUp, ChevronDown, Check, Pencil, Trash2, ImageIcon } from "lucide-react";

// Derive a template's example-image thumbnail using the same field priority as
// the Ads Express picker: meme reference image → adgen example image → legacy
// example image → first normalized `exampleImages` entry.
function templateThumbUrl(unit: AdUnitType): string | null {
  if ((unit as any).isMeme === true) {
    const meme = (unit as any).memeReferenceImageUrl;
    if (typeof meme === "string" && meme.trim()) return meme;
  }
  const adgen = (unit as any).adgenExampleImageUrl;
  if (typeof adgen === "string" && adgen.trim()) return adgen;
  const legacy = (unit as any).exampleImageUrl;
  if (typeof legacy === "string" && legacy.trim()) return legacy;
  const records = Array.isArray((unit as any).exampleImages)
    ? (unit as any).exampleImages
        .map((e: unknown) => normalizeExampleImage(e))
        .filter((r: unknown): r is NonNullable<typeof r> => r != null)
    : [];
  return displayUrl(records[0] ?? null);
}

export function TemplateCategoriesManager() {
  const { toast } = useToast();
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(CATEGORY_COLOR_OPTIONS[0].key);
  const [newIcon, setNewIcon] = useState("folder");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkCategoryId, setBulkCategoryId] = useState<string>("none");
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);

  const { data: categories = [], isLoading } = useQuery<TemplateCategory[]>({
    queryKey: ["/api/template-categories"],
  });
  const { data: allAdUnits = [], isLoading: adUnitsLoading } = useQuery<AdUnitType[]>({
    queryKey: ["/api/admin/ad-units"],
  });

  // Invalidate every ad-unit list (admin list + each brand's
  // `/api/brands/:brandId/ad-units` that the Ads Express picker reads) so
  // category assignments/deletions reflect in the picker without a reload.
  const invalidateAdUnits = () => {
    queryClient.invalidateQueries({
      predicate: (q) =>
        typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).includes("/ad-units"),
    });
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/template-categories"] });
    invalidateAdUnits();
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/admin/template-categories", {
        name: newName.trim(),
        color: newColor,
        icon: newIcon,
      });
    },
    onSuccess: () => {
      invalidateAll();
      setNewName("");
      toast({ title: "Category created" });
    },
    onError: (e: Error) => toast({ title: "Failed to create category", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TemplateCategory> }) => {
      await apiRequest("PATCH", `/api/admin/template-categories/${id}`, data);
    },
    onSuccess: () => {
      invalidateAll();
      setEditingId(null);
      setEditingName("");
    },
    onError: (e: Error) => toast({ title: "Failed to update category", description: e.message, variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await apiRequest("POST", "/api/admin/template-categories/reorder", { orderedIds });
    },
    onSuccess: () => invalidateAll(),
    onError: (e: Error) => toast({ title: "Failed to reorder", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/template-categories/${id}`);
    },
    onSuccess: () => {
      invalidateAll();
      setDeleteId(null);
      toast({ title: "Category deleted", description: "Its templates are now uncategorized." });
    },
    onError: (e: Error) => toast({ title: "Failed to delete category", description: e.message, variant: "destructive" }),
  });

  const assignMutation = useMutation({
    mutationFn: async ({ adUnitId, categoryId }: { adUnitId: string; categoryId: string | null }) => {
      await apiRequest("PATCH", `/api/admin/ad-units/${adUnitId}/category`, { categoryId });
    },
    onSuccess: () => {
      invalidateAdUnits();
    },
    onError: (e: Error) => toast({ title: "Failed to assign category", description: e.message, variant: "destructive" }),
  });

  const bulkAssignMutation = useMutation({
    mutationFn: async ({ ids, categoryId }: { ids: string[]; categoryId: string | null }) => {
      for (const adUnitId of ids) {
        await apiRequest("PATCH", `/api/admin/ad-units/${adUnitId}/category`, { categoryId });
      }
    },
    onSuccess: (_data, vars) => {
      invalidateAdUnits();
      setSelectedIds(new Set());
      const target = vars.categoryId ? categories.find((c) => c.id === vars.categoryId)?.name ?? "category" : "Uncategorized";
      toast({ title: `Updated ${vars.ids.length} template${vars.ids.length === 1 ? "" : "s"}`, description: `Moved to ${target}.` });
    },
    onError: (e: Error) => toast({ title: "Failed to assign categories", description: e.message, variant: "destructive" }),
  });

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= categories.length) return;
    const ids = categories.map((c) => c.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderMutation.mutate(ids);
  };

  const countByCategory = (id: string) => allAdUnits.filter((au) => (au as any).categoryId === id).length;

  const assignableUnits = allAdUnits
    .filter((au) => !au.brandId && !au.archivedAt)
    .filter((au) => {
      const q = assignSearch.trim().toLowerCase();
      if (!q) return true;
      return (au.name ?? "").toLowerCase().includes(q) || (au.slug ?? "").toLowerCase().includes(q);
    });

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const visibleIds = assignableUnits.map((au) => au.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  return (
    <div className="space-y-6 min-w-0" data-testid="section-categories">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Create Template Category</CardTitle>
          <CardDescription>
            Custom categories group templates in the Ads Express picker. Assigned templates show under their category (in this order); unassigned templates fall back to the automatic grouping.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs">Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Category name (e.g. Seasonal Promos)"
                data-testid="input-new-category-name"
                onKeyDown={(e) => { if (e.key === "Enter" && newName.trim()) createMutation.mutate(); }}
              />
            </div>
            <div className="w-32">
              <Label className="text-xs">Color</Label>
              <Select value={newColor} onValueChange={setNewColor}>
                <SelectTrigger data-testid="select-new-category-color"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_COLOR_OPTIONS.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      <span className="flex items-center gap-2">
                        <span className={["h-3 w-3 rounded-full", c.bar].join(" ")} />
                        {c.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Label className="text-xs">Icon</Label>
              <Select value={newIcon} onValueChange={setNewIcon}>
                <SelectTrigger data-testid="select-new-category-icon"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_ICON_OPTIONS.map((i) => (
                    <SelectItem key={i.key} value={i.key}>
                      <span className="flex items-center gap-2">{i.icon}{i.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!newName.trim() || createMutation.isPending}
              className="rounded-full gap-1.5"
              data-testid="button-create-category"
            >
              <Plus className="h-4 w-4" />
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-lg font-medium mb-3">Categories</h2>
        {isLoading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : categories.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="text-no-categories">
              No categories yet. Create the first one above.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {categories.map((c, index) => {
              const visual = getCategoryVisual(c.color, c.icon);
              return (
                <Card key={c.id} data-testid={`card-category-${c.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className={["h-9 w-9 rounded-lg flex items-center justify-center shrink-0", visual.accent].join(" ")}>
                          {visual.icon}
                        </div>
                        {editingId === c.id ? (
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="h-8 text-sm max-w-xs"
                            autoFocus
                            data-testid={`input-rename-category-${c.id}`}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && editingName.trim()) {
                                updateMutation.mutate({ id: c.id, data: { name: editingName.trim() } });
                              } else if (e.key === "Escape") { setEditingId(null); setEditingName(""); }
                            }}
                          />
                        ) : (
                          <div className="min-w-0">
                            <p className="font-medium truncate" data-testid={`text-category-name-${c.id}`}>{c.name}</p>
                            <p className="text-xs text-muted-foreground">{countByCategory(c.id)} template{countByCategory(c.id) === 1 ? "" : "s"}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Select value={c.color ?? CATEGORY_COLOR_OPTIONS[0].key} onValueChange={(v) => updateMutation.mutate({ id: c.id, data: { color: v } })}>
                          <SelectTrigger className="h-8 w-28" data-testid={`select-category-color-${c.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CATEGORY_COLOR_OPTIONS.map((opt) => (
                              <SelectItem key={opt.key} value={opt.key}>
                                <span className="flex items-center gap-2"><span className={["h-3 w-3 rounded-full", opt.bar].join(" ")} />{opt.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={c.icon ?? "folder"} onValueChange={(v) => updateMutation.mutate({ id: c.id, data: { icon: v } })}>
                          <SelectTrigger className="h-8 w-32" data-testid={`select-category-icon-${c.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CATEGORY_ICON_OPTIONS.map((opt) => (
                              <SelectItem key={opt.key} value={opt.key}>
                                <span className="flex items-center gap-2">{opt.icon}{opt.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={index === 0 || reorderMutation.isPending} onClick={() => move(index, -1)} data-testid={`button-category-up-${c.id}`}>
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={index === categories.length - 1 || reorderMutation.isPending} onClick={() => move(index, 1)} data-testid={`button-category-down-${c.id}`}>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        {editingId === c.id ? (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => updateMutation.mutate({ id: c.id, data: { name: editingName.trim() } })} data-testid={`button-save-category-${c.id}`}>
                            <Check className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingId(c.id); setEditingName(c.name); }} data-testid={`button-rename-category-${c.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteId(c.id)} data-testid={`button-delete-category-${c.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-medium">Assign Templates</h2>
          <Input
            value={assignSearch}
            onChange={(e) => setAssignSearch(e.target.value)}
            placeholder="Search templates..."
            className="h-9 max-w-xs"
            data-testid="input-assign-search"
          />
        </div>
        <p className="text-sm text-muted-foreground mb-4">Each template can belong to one category. Set to "Uncategorized" to fall back to automatic grouping. Tick several templates to assign them all at once.</p>
        {adUnitsLoading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : assignableUnits.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="text-no-assignable">No templates found.</p>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                  onCheckedChange={toggleSelectAll}
                  data-testid="checkbox-select-all-templates"
                  aria-label="Select all templates"
                />
                <span className="text-sm text-muted-foreground" data-testid="text-selected-count">
                  {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select all"}
                </span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <SearchableSelect
                  value={bulkCategoryId}
                  onValueChange={setBulkCategoryId}
                  placeholder="Move to..."
                  testId="select-bulk-category"
                  triggerClassName="h-8 w-48"
                  options={[
                    { value: "none", label: "Uncategorized" },
                    ...categories.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                />
                <Button
                  size="sm"
                  className="rounded-full"
                  disabled={selectedIds.size === 0 || bulkAssignMutation.isPending}
                  onClick={() => bulkAssignMutation.mutate({ ids: Array.from(selectedIds), categoryId: bulkCategoryId === "none" ? null : bulkCategoryId })}
                  data-testid="button-bulk-assign"
                >
                  {bulkAssignMutation.isPending ? "Applying..." : `Apply${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`}
                </Button>
                {selectedIds.size > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="button-clear-selection">
                    Clear
                  </Button>
                )}
              </div>
            </div>
            {assignableUnits.map((au) => (
              <Card key={au.id} data-testid={`row-assign-${au.id}`}>
                <CardContent className="p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Checkbox
                      checked={selectedIds.has(au.id)}
                      onCheckedChange={() => toggleSelected(au.id)}
                      data-testid={`checkbox-template-${au.id}`}
                      aria-label={`Select ${au.name}`}
                    />
                    {(() => {
                      const thumb = templateThumbUrl(au);
                      return thumb ? (
                        <button
                          type="button"
                          onClick={() => setPreview({ url: thumb, name: au.name })}
                          className="h-12 w-12 shrink-0 rounded-md overflow-hidden border border-border/50 bg-muted/30 cursor-zoom-in transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={`View larger image of ${au.name}`}
                          data-testid={`button-assign-thumb-${au.id}`}
                        >
                          <AdImage
                            src={thumb}
                            alt={au.name}
                            className="h-full w-full object-contain"
                            placeholderClassName="h-12 w-12 min-h-0 min-w-0"
                            data-testid={`img-assign-thumb-${au.id}`}
                          />
                        </button>
                      ) : (
                        <div
                          className="h-12 w-12 shrink-0 rounded-md border border-border/50 bg-muted/30 flex items-center justify-center"
                          data-testid={`img-assign-thumb-${au.id}`}
                        >
                          <ImageIcon className="h-5 w-5 text-muted-foreground/40" />
                        </div>
                      );
                    })()}
                    <p className="text-sm truncate min-w-0" data-testid={`text-assign-name-${au.id}`}>{au.name}</p>
                  </div>
                  <SearchableSelect
                    value={(au as any).categoryId ?? "none"}
                    onValueChange={(v) => assignMutation.mutate({ adUnitId: au.id, categoryId: v === "none" ? null : v })}
                    testId={`select-assign-category-${au.id}`}
                    triggerClassName="h-8 w-48 shrink-0"
                    options={[
                      { value: "none", label: "Uncategorized" },
                      ...categories.map((c) => ({ value: c.id, label: c.name })),
                    ]}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category?</AlertDialogTitle>
            <AlertDialogDescription>
              Templates in this category will become uncategorized (they won't be deleted). This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-category">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteId && deleteMutation.mutate(deleteId)} data-testid="button-confirm-delete-category">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!preview} onOpenChange={(open) => { if (!open) setPreview(null); }}>
        <DialogContent className="max-w-2xl" data-testid="dialog-template-preview">
          <DialogHeader>
            <DialogTitle className="truncate" data-testid="text-preview-name">{preview?.name}</DialogTitle>
          </DialogHeader>
          {preview && (
            <AdImage
              src={preview.url}
              alt={preview.name}
              className="w-full max-h-[70vh] rounded-md object-contain bg-muted/30"
              data-testid="img-template-preview"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
